'use client';

import React from 'react';
import { Users, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { UserItem } from '../types';

interface UsersTabProps {
  users: UserItem[];
  userFilter: 'ALL' | 'ORGANIZATION' | 'MEMBER' | 'SUSPENDED';
  setUserFilter: (filter: 'ALL' | 'ORGANIZATION' | 'MEMBER' | 'SUSPENDED') => void;
  usersTotal: number;
  usersPage: number;
  setUsersPage: React.Dispatch<React.SetStateAction<number>>;
  usersTotalPages: number;
  pageSize: number;
  loading: boolean;
  searchTerm: string;
  handleToggleUserStatus: (userId: string, currentStatus: string) => void;
  setUserToDelete: (user: { id: string; name: string; email: string }) => void;
  handleOpenWorkspaceBoards?: (wsId: string) => void;
  onSelectUser: (user: UserItem) => void;
}

export const UsersTab: React.FC<UsersTabProps> = ({
  users,
  userFilter,
  setUserFilter,
  usersTotal,
  usersPage,
  setUsersPage,
  usersTotalPages,
  pageSize,
  loading,
  searchTerm,
  handleToggleUserStatus,
  setUserToDelete,
  handleOpenWorkspaceBoards,
  onSelectUser,
}) => {
  return (
    <div className="bg-[#0A0D1A] border border-slate-800/80 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-black text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-400" />
            <span>Global User & Organization Directory</span>
          </h2>
          <p className="text-xs text-slate-400 mt-1">Manage user account statuses and click on any row to inspect complete user details.</p>
        </div>

        <div className="flex items-center gap-1 bg-[#060811] border border-slate-800 p-1 rounded-2xl">
          {(['ALL', 'ORGANIZATION', 'MEMBER', 'SUSPENDED'] as const).map((filter) => (
            <button
              key={filter}
              onClick={() => {
                setUserFilter(filter);
                setUsersPage(1);
              }}
              className={`px-3 py-1.5 rounded-xl text-[10px] font-extrabold transition-all cursor-pointer ${userFilter === filter
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'text-slate-400 hover:text-slate-200'
                }`}
            >
              {filter}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs border-collapse">
          <thead>
            <tr className="border-b border-slate-800 text-slate-400 uppercase text-[10px] tracking-wider">
              <th className="py-3.5 px-4 font-bold">User Name</th>
              <th className="py-3.5 px-4 font-bold">Email</th>
              <th className="py-3.5 px-4 font-bold">Type</th>
              <th className="py-3.5 px-4 font-bold">Account Status</th>
              <th className="py-3.5 px-4 font-bold">KYC Status</th>
              <th className="py-3.5 px-4 font-bold text-right">Account Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {loading ? (
              <tr>
                <td colSpan={6} className="py-12 text-center text-slate-400 font-medium">
                  <div className="inline-flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                    <span>Fetching users from backend database...</span>
                  </div>
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-12 text-center text-slate-500 font-medium">
                  No users found matching backend query "{searchTerm}".
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <tr
                  key={u.id}
                  onClick={() => onSelectUser(u)}
                  className="hover:bg-[#060811] transition-colors cursor-pointer group"
                >
                  <td className="py-4 px-4 font-bold text-white flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-indigo-500/20 text-indigo-400 flex items-center justify-center font-black text-xs uppercase group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                      {u.name?.[0] || 'U'}
                    </div>
                    <span className="group-hover:text-indigo-300 transition-colors">{u.name}</span>
                    {u.isSuperadmin && (
                      <span className="px-1.5 py-0.5 bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 text-[9px] font-black rounded">
                        SUPERADMIN
                      </span>
                    )}
                  </td>
                  <td className="py-4 px-4 text-slate-300 font-mono">{u.email}</td>
                  <td className="py-4 px-4">
                    <div className="space-y-1">
                      <span className="px-2 py-0.5 bg-slate-800 text-slate-300 text-[10px] font-bold rounded-md inline-block">
                        {u.userType}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span
                      className={`px-2.5 py-1 text-[10px] font-black rounded-lg ${u.accountStatus === 'ACTIVE'
                        ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                        : u.accountStatus === 'SUSPENDED'
                          ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
                          : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                        }`}
                    >
                      {u.accountStatus}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-slate-400">{u.kycStatus}</td>
                  <td className="py-4 px-4 text-right flex items-center justify-end gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleUserStatus(u.id, u.accountStatus);
                      }}
                      className={`px-3 py-1.5 text-[10px] font-black rounded-xl transition-all cursor-pointer ${u.accountStatus === 'ACTIVE'
                        ? 'bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-400'
                        : 'bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-400'
                        }`}
                    >
                      {u.accountStatus === 'ACTIVE' ? 'Suspend' : 'Activate'}
                    </button>

                    {!u.isSuperadmin && u.email !== 'admin@cognoflux.com' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setUserToDelete({ id: u.id, name: u.name, email: u.email });
                        }}
                        className="px-2.5 py-1.5 bg-rose-600/20 hover:bg-rose-600/30 border border-rose-500/40 text-rose-300 font-extrabold text-[10px] rounded-xl flex items-center gap-1 transition-all cursor-pointer"
                        title="Delete User Permanently"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        <span>Delete</span>
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Users Backend Pagination Bar */}
      {usersTotalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-800/80">
          <div className="text-xs text-slate-400 font-medium">
            Showing <strong className="text-white">{(usersPage - 1) * pageSize + 1}</strong> to{' '}
            <strong className="text-white">{Math.min(usersPage * pageSize, usersTotal)}</strong> of{' '}
            <strong className="text-white">{usersTotal}</strong> users
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setUsersPage((p) => Math.max(p - 1, 1))}
              disabled={usersPage === 1}
              className="px-3.5 py-1.5 bg-[#060811] hover:bg-slate-800 disabled:opacity-40 border border-slate-800 rounded-xl text-xs font-bold text-slate-300 flex items-center gap-1 transition-all cursor-pointer"
            >
              <ChevronLeft className="w-4 h-4" /> Previous
            </button>

            <span className="text-xs font-mono font-bold text-slate-300 px-3">
              Page {usersPage} of {usersTotalPages}
            </span>

            <button
              onClick={() => setUsersPage((p) => Math.min(p + 1, usersTotalPages))}
              disabled={usersPage >= usersTotalPages}
              className="px-3.5 py-1.5 bg-[#060811] hover:bg-slate-800 disabled:opacity-40 border border-slate-800 rounded-xl text-xs font-bold text-slate-300 flex items-center gap-1 transition-all cursor-pointer"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
